/**
 * The patient's identity history, as the frontend sees it (DECISIONS.md #45).
 *
 * Mirrors `api/src/Continuum.Api/Patients/PatientVersions.cs`.
 *
 * **The demographic counterpart of `fetchNoteVersions`**, and it exists for the same reason: #43
 * corrects a patient by appending version 2, and "the old one is still there" is a property of the
 * record that nobody can see until something returns it. Before this, the only reader of a
 * superseded `clinical.patient` row was `--verify-audit`.
 *
 * **Values and codes, never rendered text** (I18N.md §1), like the overview beside it. The birth
 * date is a calendar date and the sex is a code; turning either into words is
 * `patientVersionsDisplay.ts`.
 *
 * **Asked for, not prefetched.** Reading it writes an audit row (AGENTS.md §4), so opening a
 * patient must not fetch a history nobody looked at and claim somebody read it. The row this
 * writes is truthful: a clinician did ask to see how the identity got to where it is.
 */

import { unauthenticated } from '../auth/sessionExpiry'

/**
 * One version of a patient's demographics. Mirrors `PatientVersionView`.
 *
 * Narrower than the overview's `PatientOverview` in one direction and wider in another: no
 * identifiers, no ward, no notes — this is the history of one row in one table — but it carries the
 * row identity and the two columns a correction inherits rather than sets.
 */
export interface PatientVersion {
  /** `clinical.patient.version_id` — this exact row, and what a correction supersedes. */
  readonly versionId: string

  readonly version: number

  /** The `version_id` this version replaced, or null for version 1. */
  readonly supersedes: string | null

  /** `active`, `inactive` or `entered-in-error`. */
  readonly status: string

  readonly givenName: string | null
  readonly familyName: string | null

  /** `YYYY-MM-DD`, or null when the identity was not established at this version. */
  readonly birthDate: string | null

  /** HL7 administrative gender, or null. `unknown` is a recorded value, not an absence. */
  readonly sex: string | null

  /**
   * Whether this version calls the identity a working assumption.
   *
   * The column a history is usually opened for. #43's case is the patient who arrives unidentified
   * and is named later, and the version where this went false is the moment the record stopped
   * hedging.
   */
  readonly isEstimatedIdentity: boolean

  /** sekretessmarkering — inherited by a correction, never set by one. */
  readonly confidentialityMarked: boolean

  /** The same on every version: a correction inherits version 1's. */
  readonly effectiveAt: string

  /** When this version was written. What differs between versions. */
  readonly recordedAt: string

  readonly authorGivenName: string | null
  readonly authorFamilyName: string | null
  readonly authorRoleTitle: string | null
}

/** A patient's whole identity history. Mirrors `PatientVersions`. */
export interface PatientHistory {
  readonly patientId: string

  /** Newest first. Never empty — a patient with no versions is a 404. */
  readonly versions: readonly PatientVersion[]
}

/** Why a history could not be read. */
export type PatientHistoryFailure = 'not-found' | 'unavailable'

/**
 * The history could not be read.
 *
 * A failure union rather than a status code, matching `NoteHistoryError`: the screen has two things
 * to say, and which one is not a property of HTTP.
 */
export class PatientHistoryError extends Error {
  readonly failure: PatientHistoryFailure

  constructor(failure: PatientHistoryFailure) {
    super(`Patient history unavailable: ${failure}`)
    this.name = 'PatientHistoryError'
    this.failure = failure
  }
}

/**
 * Every version of one patient's demographics, newest first.
 *
 * **This is a read of the record and it writes an audit row** (AGENTS.md §4) — see the module
 * remarks on why it is not prefetched.
 *
 * Served on the patient's `/versions` collection, which is also where a correction is posted. There
 * is no second URL, unlike a note's history: `/api/patients/{id}` is the overview.
 */
export async function fetchPatientVersions(
  patientId: string,
  signal: AbortSignal,
): Promise<PatientHistory> {
  let response: Response

  try {
    response = await fetch(`/api/patients/${encodeURIComponent(patientId)}/versions`, {
      signal,
      headers: { Accept: 'application/json' },
    })
  } catch (cause) {
    if (signal.aborted) {
      throw cause
    }
    throw new PatientHistoryError('unavailable')
  }

  if (response.status === 404) {
    throw new PatientHistoryError('not-found')
  }

  // Before the general case, because a 401 is not an unavailable box: it means the session has
  // ended, and `SessionGate` is about to show the login screen. Reported rather than rendered —
  // see `auth/sessionExpiry.ts`. The throw still happens, so this screen does not sit
  // half-updated in the moment before it unmounts.
  unauthenticated(response)

  if (!response.ok) {
    throw new PatientHistoryError('unavailable')
  }

  try {
    return (await response.json()) as PatientHistory
  } catch {
    throw new PatientHistoryError('unavailable')
  }
}
